import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Pane, useMap } from 'react-leaflet';
import L from 'leaflet';
import RouteLayer from './RouteLayer';
import VehicleLayer from './VehicleLayer';
import FleetVehicleLayer from './FleetVehicleLayer';
import StaticContentLayer from './StaticContentLayer';
import ResetZoomButton from './ResetZoomButton';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import VehicleStatusDrawer from './VehicleMessage';
import RouteStatusDrawer from './RouteStatusDrawer';
import VenueToggleDrawer from './VenueToggleDrawer';
import EventFocusDrawer from './EventFocusDrawer';
import DistrictLayerDrawer from './DistrictLayerDrawer';
import CupDrawer from './CupDrawer';
import MapEventHandler from './MapEventHandler';
import useExternalRoutes from '../hooks/useExternalRoutes';

// Import route data
import routesData from '../data/routes.json';

// API Configuration
const API_CONFIG = {
  baseUrl: process.env.NODE_ENV === 'production' 
    ? 'https://webservices.umoiq.com/api/pub/v1' 
    : '/api/pub/v1', // Use relative URL for proxy in development
  agencyId: 'sfmta-cis',
  apiKey: process.env.REACT_APP_UMO_API_KEY,
  pollInterval: 15000 // 15 seconds
};

const CenterOnUserLocationControl = ({ userLocation, setUserLocation, targetZoom }) => {
  const map = useMap();

  useEffect(() => {
    const control = L.control({ position: 'topleft' });

    control.onAdd = () => {
      const container = L.DomUtil.create('div', 'leaflet-bar');
      container.style.marginTop = '6px';

      const button = L.DomUtil.create('a', '', container);
      button.href = '#';
      button.setAttribute('role', 'button');
      button.setAttribute('aria-label', 'Center map on your location');
      button.title = 'Center on your location';
      button.style.width = '30px';
      button.style.height = '30px';
      button.style.lineHeight = '30px';
      button.style.textAlign = 'center';
      button.style.fontSize = '16px';
      button.style.fontWeight = '700';
      button.style.color = '#333';
      button.style.background = '#fff';
      button.innerHTML = '⌖';

      L.DomEvent.disableClickPropagation(container);
      L.DomEvent.disableScrollPropagation(container);

      const handleClick = (e) => {
        L.DomEvent.preventDefault(e);

        const z = Number(targetZoom);
        const nextZoom = Number.isFinite(z) ? z : map.getZoom();

        if (Array.isArray(userLocation) && userLocation.length === 2) {
          map.setView(userLocation, nextZoom, { animate: true });
          return;
        }

        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const next = [pos.coords.latitude, pos.coords.longitude];
            if (typeof setUserLocation === 'function') setUserLocation(next);
            map.setView(next, nextZoom, { animate: true });
          },
          () => {
            // Ignore; user may have denied.
          },
          { enableHighAccuracy: true, maximumAge: 30000, timeout: 10000 }
        );
      };

      button.addEventListener('click', handleClick);

      return container;
    };

    control.addTo(map);
    return () => {
      control.remove();
    };
  }, [map, userLocation, setUserLocation, targetZoom]);

  return null;
};



const CampaignMap = () => {
  const { campaignName } = useParams();
  const mapRef = useRef();
  const staticMarkerRefMapRef = useRef(new Map());
  const clusterGroupRef = useRef();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousOverscroll = document.body.style.overscrollBehavior;
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.overscrollBehavior = previousOverscroll;
    };
  }, []);

  // TV mode via URL query param: /:campaignName?tv=true
  const isTvMode = (searchParams.get('tv') || '').toLowerCase() === 'true';

  // Hard-coded TV bounds (adjust as needed)
  // These bounds roughly cover San Francisco; replace with your desired extent
  const TV_BOUNDS = useMemo(() => L.latLngBounds([
    [37.70120736474139, -122.55249023437501],
    [37.80625771945958, -122.20470428466798]
  ]), []);

  // Track if we've already applied the TV bounds once
  const hasAppliedTvBounds = useRef(false);

  // Track if we've applied bounds once for route tracking modes (routes_only / vehicle_and_routes)
  const hasAppliedRouteTrackingBounds = useRef(false);

  // In route tracking modes, stop auto-fitting after the user manually moves/zooms the map
  const hasUserInteractedWithMap = useRef(false);
  const suppressNextMoveEnd = useRef(false);

  // Campaign data state
  const [campaignData, setCampaignData] = useState({
    vehicles: [],
    content: null,
    routesConfig: null,
    routesFleet: null,
    config: null,
    staticContent: null
  });

  // Live data state
  const [activeVehicles, setActiveVehicles] = useState([]);
  const [tripRoutes, setTripRoutes] = useState([]);
  const [fleetVehicles, setFleetVehicles] = useState([]);
  const [fleetTripRoutes, setFleetTripRoutes] = useState([]);

  const [activeFleetRouteIds, setActiveFleetRouteIds] = useState(new Set());

  // External routes from 511 cacher (or other external sources)
  const externalRoutesConfig = useMemo(() => {
    return campaignData.config && campaignData.config.externalRoutes
      ? campaignData.config.externalRoutes
      : null;
  }, [campaignData.config]);
  const { externalRoutes, externalVehicles } = useExternalRoutes(externalRoutesConfig);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showResetButton, setShowResetButton] = useState(false);
  const [userLocation, setUserLocation] = useState(null);

  // Map bounds for reset functionality
  const [defaultBounds, setDefaultBounds] = useState(null);

  // Load campaign data on mount or campaign change
  useEffect(() => {
    loadCampaignData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignName]);

  const campaignMode = (campaignData.config && campaignData.config.mode) || 'vehicles_only';
  const activeModeConfig = (campaignData.config && campaignData.config.modes && campaignData.config.modes[campaignMode]) || {};
  const showVehicleTray = (activeModeConfig.ui && activeModeConfig.ui.tray && typeof activeModeConfig.ui.tray.vehicle === 'boolean')
    ? activeModeConfig.ui.tray.vehicle
    : true;

  const showRouteTray = (activeModeConfig.ui && activeModeConfig.ui.tray && typeof activeModeConfig.ui.tray.route === 'boolean')
    ? activeModeConfig.ui.tray.route
    : false;

  const vehicleArrowColor = (activeModeConfig.ui && typeof activeModeConfig.ui.vehicle_arrow_color === 'string')
    ? activeModeConfig.ui.vehicle_arrow_color
    : null;

  const isEventFocusMode = campaignMode === 'event_focus';
  const isStaticOnlyMode = campaignMode === 'static_only';
  const isRoutesOnlyMode = campaignMode === 'routes_only' || isEventFocusMode;
  const isVehicleAndRoutesMode = campaignMode === 'vehicle_and_routes';
  const shouldPollVehiclesOnly = (campaignMode === 'vehicles_only' || isVehicleAndRoutesMode) && !isStaticOnlyMode;
  const shouldPollRouteFleets = (isRoutesOnlyMode || isVehicleAndRoutesMode) && !isStaticOnlyMode;

  useEffect(() => {
    const routesFleet = campaignData.routesFleet;
    const ids = new Set();

    // In event_focus mode, start with all routes OFF — user opts in
    if (isEventFocusMode) {
      setActiveFleetRouteIds(ids);
      return;
    }

    // Add internal fleet route IDs
    if (Array.isArray(routesFleet)) {
      for (const r of routesFleet) {
        if (r && r.id != null) ids.add(String(r.id));
      }
    }

    // Add external route IDs
    if (Array.isArray(externalRoutes)) {
      for (const r of externalRoutes) {
        if (r && r.id != null) ids.add(String(r.id));
      }
    }

    setActiveFleetRouteIds(ids);
  }, [campaignName, campaignData.routesFleet, externalRoutes, isEventFocusMode]);

  const toggleFleetRoute = (routeId) => {
    const key = String(routeId);
    setActiveFleetRouteIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // When switching campaigns or modes, allow initial auto-fit again for route tracking modes
  useEffect(() => {
    hasAppliedRouteTrackingBounds.current = false;
    hasUserInteractedWithMap.current = false;
    suppressNextMoveEnd.current = false;
  }, [campaignName, campaignMode]);

  const staticOverlaysEnabled = Boolean(campaignData.config && campaignData.config.staticOverlays && campaignData.config.staticOverlays.enabled);

  const staticOverlayTrayConfig = (campaignData.config
    && campaignData.config.staticOverlays
    && campaignData.config.staticOverlays.ui
    && campaignData.config.staticOverlays.ui.tray)
    ? campaignData.config.staticOverlays.ui.tray
    : null;

  // Venue-toggle mode: toggles are per-venue instead of per-route
  const venueToggleMode = Boolean(staticOverlayTrayConfig && staticOverlayTrayConfig.venueToggleMode);

  // Track which venues are active (by venue id). All on by default.
  const [activeVenueIds, setActiveVenueIds] = useState(new Set());

  // Build a lookup: venueId → Set of route-id strings
  const venueRouteMap = useMemo(() => {
    const map = new Map();
    const pts = campaignData.staticContent && Array.isArray(campaignData.staticContent.point_data)
      ? campaignData.staticContent.point_data
      : [];
    for (const pt of pts) {
      if (!pt || !pt.id) continue;
      const corr = pt.cooresponding_route;
      if (corr == null) continue;
      const ids = String(corr).split(',').map(s => s.trim()).filter(Boolean);
      map.set(String(pt.id), new Set(ids));
    }
    return map;
  }, [campaignData.staticContent]);

  // Initialise activeVenueIds when static content loads (all venues on)
  useEffect(() => {
    if (!venueToggleMode) return;
    const pts = campaignData.staticContent && Array.isArray(campaignData.staticContent.point_data)
      ? campaignData.staticContent.point_data
      : [];
    setActiveVenueIds(new Set(pts.filter(p => p && p.id).map(p => String(p.id))));
  }, [venueToggleMode, campaignData.staticContent]);

  // In venue-toggle mode, derive activeFleetRouteIds from active venues
  useEffect(() => {
    if (!venueToggleMode) return;
    const routeIds = new Set();
    for (const venueId of activeVenueIds) {
      const routes = venueRouteMap.get(venueId);
      if (routes) {
        for (const r of routes) routeIds.add(r);
      }
    }
    setActiveFleetRouteIds(routeIds);
  }, [venueToggleMode, activeVenueIds, venueRouteMap]);

  const toggleVenue = (venueId) => {
    const key = String(venueId);
    setActiveVenueIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // --- District layer state (static_only mode) ---
  const districtLayerConfig = (campaignData.config && campaignData.config.districtLayers) || null;
  const districtLayersEnabled = Boolean(districtLayerConfig && districtLayerConfig.enabled);
  const districtList = useMemo(() => {
    return (districtLayerConfig && Array.isArray(districtLayerConfig.districts))
      ? districtLayerConfig.districts
      : [];
  }, [districtLayerConfig]);

  // Type visibility toggles (both on by default)
  const [showMatchHQ, setShowMatchHQ] = useState(true);
  const [showHiddenGem, setShowHiddenGem] = useState(true);
  const [showWatchParties, setShowWatchParties] = useState(true);

  // Cup campaign: venue/country visibility
  const [showCupVenues, setShowCupVenues] = useState(true);
  const [enabledCountries, setEnabledCountries] = useState(new Set());
  const [cupFiltersActive, setCupFiltersActive] = useState(false);
  const [cupFiltersEverActivated, setCupFiltersEverActivated] = useState(false);

  // Build district name → id lookup
  const districtNameToId = useMemo(() => {
    const map = new Map();
    if (districtLayerConfig && Array.isArray(districtLayerConfig.districts)) {
      for (const d of districtLayerConfig.districts) {
        map.set(d.name, d.id);
      }
    }
    return map;
  }, [districtLayerConfig]);

  // Zoom map to fit all pins in a given district
  const zoomToDistrict = useCallback((districtId) => {
    if (!mapRef.current || !campaignData.staticContent) return;
    const pts = campaignData.staticContent.point_data || [];
    const district = districtList.find(d => d.id === districtId);
    if (!district) return;

    const coords = pts
      .filter(pt => pt && pt.district === district.name)
      .filter(pt => Number.isFinite(Number(pt.lat)) && Number.isFinite(Number(pt.lng)))
      .map(pt => [Number(pt.lat), Number(pt.lng)]);

    if (coords.length === 0) return;

    const bounds = L.latLngBounds(coords);
    mapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 16, animate: true });
  }, [campaignData.staticContent, districtList]);

  // Start vehicle polling after campaign data loads
  useEffect(() => {
    if (shouldPollVehiclesOnly && campaignData.vehicles.length > 0) {
      const interval = setInterval(fetchLiveVehicleData, API_CONFIG.pollInterval);
      fetchLiveVehicleData(); // Initial fetch
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldPollVehiclesOnly, campaignData.vehicles]);

  // Start route fleet polling after campaign data loads
  useEffect(() => {
    const routesFleet = campaignData.routesFleet;
    if (shouldPollRouteFleets && Array.isArray(routesFleet) && routesFleet.length > 0) {
      const interval = setInterval(fetchLiveFleetVehicleData, API_CONFIG.pollInterval);
      fetchLiveFleetVehicleData(); // Initial fetch
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldPollRouteFleets, campaignData.routesFleet]);

  // Load default-trip shapes/stops for each fleet route (routes_only / vehicle_and_routes)
  useEffect(() => {
    const routesFleet = campaignData.routesFleet;
    if (!shouldPollRouteFleets || !Array.isArray(routesFleet) || routesFleet.length === 0) {
      setFleetTripRoutes([]);
      return;
    }

    loadFleetDefaultRoutes(routesFleet);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldPollRouteFleets, campaignData.routesFleet]);

  // Request user location on mount (or use ?loc=lat,lng URL param to spoof)
  useEffect(() => {
    const locParam = searchParams.get('loc');
    if (locParam) {
      const parts = locParam.split(',').map(s => parseFloat(s.trim()));
      if (parts.length === 2 && Number.isFinite(parts[0]) && Number.isFinite(parts[1])) {
        setUserLocation(parts);
        return;
      }
    }
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation([
            position.coords.latitude,
            position.coords.longitude
          ]);
        },
        (error) => {
          console.log('Geolocation denied or failed:', error);
        }
      );
    }
  }, [searchParams]);

  const loadCampaignData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load campaign configuration (optional)
      const configUrl = `${process.env.PUBLIC_URL || ''}/campaigns/${campaignName}/campaign-config.json`;
      let configData = null;
      try {
        const configResponse = await fetch(configUrl);
        if (configResponse.ok) {
          configData = await configResponse.json();
        } else {
          console.log(`Failed to load campaign config: ${configResponse.status}`);
        }
      } catch (configErr) {
        console.log('Failed to load campaign config:', configErr);
      }

      // Load campaign content data from public URL
      const contentUrl = `${process.env.PUBLIC_URL || ''}/campaigns/${campaignName}/english.json`;
      const contentResponse = await fetch(contentUrl);
      
      if (!contentResponse.ok) {
        throw new Error(`Failed to load campaign content: ${contentResponse.status}`);
      }
      
      const contentData = await contentResponse.json();

      // Load campaign vehicle data from public URL
      // Load campaign vehicle data from public URL (skip for static_only mode)
      let vehiclesData = [];
      if (!configData || configData.mode !== 'static_only') {
        const vehiclesUrl = `${process.env.PUBLIC_URL || ''}/campaigns/${campaignName}/vehicles.json`;
        const vehiclesResponse = await fetch(vehiclesUrl);
        
        if (!vehiclesResponse.ok) {
          throw new Error(`Failed to load campaign vehicles: ${vehiclesResponse.status}`);
        }
        
        vehiclesData = await vehiclesResponse.json();
      }

      // Load static content only when enabled by campaign config
      let staticContentData = null;
      if (configData && configData.staticOverlays && configData.staticOverlays.enabled) {
        const staticContentUrl = `${process.env.PUBLIC_URL || ''}/campaigns/${campaignName}/static-content.json`;
        try {
          const staticContentResponse = await fetch(staticContentUrl);
          if (staticContentResponse.ok) {
            staticContentData = await staticContentResponse.json();
          } else {
            console.log(`Failed to load static content: ${staticContentResponse.status}`);
          }
        } catch (staticContentErr) {
          console.log('Failed to load static content:', staticContentErr);
        }
      }

      // Load campaign routes config data from public URL
      const routesConfigUrl = `${process.env.PUBLIC_URL || ''}/campaigns/${campaignName}/routes-config.json`;
      const routesConfigResponse = await fetch(routesConfigUrl);

      let routesConfigData = null;
      if (!routesConfigResponse.ok) {
        console.log(`Failed to load campaign routes config: ${routesConfigResponse.status}`);
      } else {
        routesConfigData = await routesConfigResponse.json();
      }

      // Load campaign route fleet configuration (optional)
      const routesFleetUrl = `${process.env.PUBLIC_URL || ''}/campaigns/${campaignName}/routes-fleet.json`;
      let routesFleetData = null;
      try {
        const routesFleetResponse = await fetch(routesFleetUrl);
        if (routesFleetResponse.ok) {
          routesFleetData = await routesFleetResponse.json();
        } else {
          console.log(`Failed to load campaign routes fleet: ${routesFleetResponse.status}`);
        }
      } catch (routesFleetErr) {
        console.log('Failed to load campaign routes fleet:', routesFleetErr);
      }

      // Load layer descriptions (optional, for static_only mode)
      let layerDescriptionsData = null;
      let routeColorsData = null;
      if (configData && configData.mode === 'static_only') {
        const layerDescUrl = `${process.env.PUBLIC_URL || ''}/campaigns/${campaignName}/layer-descriptions.json`;
        try {
          const layerDescResponse = await fetch(layerDescUrl);
          if (layerDescResponse.ok) {
            layerDescriptionsData = await layerDescResponse.json();
          }
        } catch (e) {
          console.log('Failed to load layer descriptions:', e);
        }

        const routeColorsUrl = `${process.env.PUBLIC_URL || ''}/campaigns/${campaignName}/routes-colors.json`;
        try {
          const routeColorsResponse = await fetch(routeColorsUrl);
          if (routeColorsResponse.ok) {
            routeColorsData = await routeColorsResponse.json();
          }
        } catch (e) {
          console.log('Failed to load route colors:', e);
        }
      }

      // Fetch external data URLs (static_only mode) — transform to point_data
      if (configData && configData.mode === 'static_only' && Array.isArray(configData.dataUrls) && configData.dataUrls.length > 0) {
        const fetchedExternalData = {};

        // Fetch all dataUrls in parallel; each one gets its own fallback
        for (const url of configData.dataUrls) {
          let data = null;
          try {
            const response = await fetch(url);
            if (response.ok) {
              data = await response.json();
              console.log(`✅ Loaded external data from: ${url}`);
            }
          } catch (e) {
            console.warn(`External data URL failed (${url}): ${e.message}`);
          }

          // Determine fallback filename from URL path (e.g. /data.json → data.json, /cup.json → cup-data.json)
          if (!data) {
            const filename = url.endsWith('/cup.json') ? 'cup-data.json' : 'goal-rush-data.json';
            const fallbackUrl = `${process.env.PUBLIC_URL || ''}/campaigns/${campaignName}/${filename}`;
            try {
              const fallbackResponse = await fetch(fallbackUrl);
              if (fallbackResponse.ok) {
                data = await fallbackResponse.json();
                console.log(`✅ Loaded fallback cache: ${filename}`);
              }
            } catch (e) {
              console.warn(`Failed to load fallback ${filename}:`, e);
            }
          }

          if (data) {
            // Identify data by shape
            if (data.bars || data.teams || data['entertainment-zones']) {
              fetchedExternalData.cup = data;
            } else if (data.merchants) {
              fetchedExternalData.merchants = data;
            }
          }
        }

        // Transform cup.json bars + viewing-licenses → point_data
        if (fetchedExternalData.cup) {
          const cupData = fetchedExternalData.cup;
          const points = [];

          // Bars → points
          if (Array.isArray(cupData.bars)) {
            for (const bar of cupData.bars) {
              if (bar.active === false) continue;
              points.push({
                id: String(bar.id),
                lat: bar.latitude,
                lng: bar.longitude,
                title: bar.name,
                address: bar.address || '',
                google_maps_url: bar.google_place_url || bar.google_maps_url || '',
                description: bar.description || '',
                'icon-emoji': '⚽',
                type: 'Bar',
                district: bar.sfgov_neighborhood || '',
                routes: (bar.transit && bar.transit.routes) || [],
                website_url: bar.website_url || '',
                phone: bar.phone || null,
                national_affinities: bar.national_affinities || [],
                legacy: bar.legacy || false,
              });
            }
          }

          // Viewing licenses → points
          if (Array.isArray(cupData['viewing-licenses'])) {
            for (const vl of cupData['viewing-licenses']) {
              points.push({
                id: String(vl.id),
                lat: vl.latitude,
                lng: vl.longitude,
                title: vl.name,
                address: '',
                google_maps_url: '',
                description: vl.description || '',
                'icon-emoji': '📺',
                type: 'Viewing License',
                district: '',
                routes: (vl.transit && vl.transit.routes) || [],
                website_url: vl.url || '',
              });
            }
          }

          if (!staticContentData) {
            staticContentData = { point_data: [], line_data: [], polygon_data: [] };
          }
          staticContentData.point_data = points;

          // Store teams for flag lookup in popups
          staticContentData._teams = cupData.teams || [];

          // Use cup route colors for popup badges
          if (cupData.routes) {
            routeColorsData = cupData.routes;
          }

          console.log(`✅ Transformed cup data: ${points.length} points (${cupData.bars ? cupData.bars.filter(b => b.active !== false).length : 0} bars, ${cupData['viewing-licenses'] ? cupData['viewing-licenses'].length : 0} viewing licenses)`);
        }
        // Transform rush merchants → point_data (when no cup data present)
        else if (fetchedExternalData.merchants) {
          const typeToEmoji = {
            'Match HQ': '⚽',
            'Hidden Gem': '💎',
            'Watch Party': '📺',
          };
          const transformed = fetchedExternalData.merchants.merchants
            .filter(m => m.active !== false)
            .map(m => ({
              id: String(m.id),
              lat: m.latitude,
              lng: m.longitude,
              title: m.merchant,
              address: m.address || '',
              google_maps_url: m.google_maps_url || '',
              description: m.promotions || '',
              'icon-emoji': typeToEmoji[m.type] || '📍',
              type: m.type || '',
              district: m.district || '',
              store_type: m.store_type || [],
              air_matches: m.air_matches || '',
              routes: (m.transit && m.transit.routes) || [],
              website_url: m.website_url || '',
              hours: m.hours || [],
              high_res_images: m.high_res_images || [],
            }));

          // Transform watchparties → point_data with type 'Watch Party'
          if (Array.isArray(fetchedExternalData.merchants.watchparties)) {
            for (const wp of fetchedExternalData.merchants.watchparties) {
              transformed.push({
                id: String(wp.id),
                lat: wp.latitude,
                lng: wp.longitude,
                title: wp.name,
                address: wp.address || '',
                google_maps_url: '',
                description: '',
                'icon-emoji': '📺',
                type: 'Watch Party',
                district: wp.district || '',
                store_type: [],
                air_matches: '',
                routes: (wp.transit && wp.transit.routes) || [],
                website_url: wp.url || '',
                hours: [],
                high_res_images: [],
              });
            }
          }

          if (!staticContentData) {
            staticContentData = { point_data: [], line_data: [], polygon_data: [] };
          }
          staticContentData.point_data = transformed;
          console.log(`✅ Transformed ${transformed.length} points into point_data (includes ${fetchedExternalData.merchants.watchparties ? fetchedExternalData.merchants.watchparties.length : 0} watch parties)`);
        }

        // Store raw data.json for future use (not rendered yet)
        if (fetchedExternalData.merchants) {
          console.log(`📦 data.json loaded (${fetchedExternalData.merchants.merchants.length} merchants) — not rendered`);
        }
      }

      setCampaignData({
        vehicles: vehiclesData,
        content: contentData,
        routesConfig: routesConfigData,
        routesFleet: routesFleetData,
        config: configData,
        staticContent: staticContentData,
        layerDescriptions: layerDescriptionsData,
        routeColors: routeColorsData
      });

      console.log(`✅ Loaded campaign "${campaignName}" with ${vehiclesData.length} vehicles`);

    } catch (err) {
      console.error('Error loading campaign data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load the default-trip route line + stops for each route in routes-fleet.json
   */
  const loadFleetDefaultRoutes = async (routesFleet) => {
    try {
      const routes = [];
      for (const route of routesFleet) {
        const routeId = route.id;
        if (!routeId) continue;

        const routeInfo = routesData[routeId];
        const defaultTripId = routeInfo && routeInfo['default-trip'];
        if (!defaultTripId) continue;

        const tripData = await fetchTripData(defaultTripId);
        if (!tripData) continue;

        routes.push({
          id: routeId,
          name: `Route ${routeId}`,
          trip_id: defaultTripId,
          coordinates: tripData.route_path
            ? tripData.route_path.coordinates.map(coord => [coord[1], coord[0]])
            : [],
          stops: tripData.stops || [],
          color: getRouteColor(routeId),
          isFallback: true,
          isFleet: true
        });
      }

      setFleetTripRoutes(routes);

      // In routes_only mode, bounds should include the fleet routes too
      if (isRoutesOnlyMode) {
        calculateMapBounds(routes, fleetVehicles);
      }
    } catch (error) {
      console.error('Error loading fleet default routes:', error);
      setFleetTripRoutes([]);
    }
  };

  /**
   * Fetch vehicles for a specific route
   */
  const fetchRouteVehicles = async (routeId) => {
    try {
      const url = `${API_CONFIG.baseUrl}/agencies/${API_CONFIG.agencyId}/routes/${routeId}/vehicles?key=${API_CONFIG.apiKey}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const vehicles = await response.json();
      console.log(`Route ${routeId}: Found ${vehicles.length} vehicles`);
      return vehicles;

    } catch (error) {
      console.error(`Error fetching vehicles for route ${routeId}:`, error);
      return [];
    }
  };

  /**
   * Fetch ALL vehicles for the agency (used for alwaysShow overrides when a vehicle
   * might not be on its typical routes, e.g. during a parade).
   */
  const fetchAllAgencyVehicles = async () => {
    try {
      const url = `${API_CONFIG.baseUrl}/agencies/${API_CONFIG.agencyId}/vehicles?key=${API_CONFIG.apiKey}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`All-vehicles API request failed: ${response.status} ${response.statusText}`);
      }

      const vehicles = await response.json();
      console.log(`🏳️‍🌈 All agency vehicles: ${vehicles.length} total`);
      return vehicles;
    } catch (error) {
      console.error('Error fetching all agency vehicles:', error);
      return [];
    }
  };

  /**
   * Fetch trip data for a specific trip ID
   */
  const fetchTripData = async (tripId) => {
    try {
      const url = `${process.env.PUBLIC_URL || ''}/transformed_gtfs/latest/trips/${tripId}.json`;
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Trip data not found: ${tripId} (Status: ${response.status})`);
      }

      const tripData = await response.json();
      console.log(`Loaded trip data for ${tripId}: ${tripData.stops.length} stops`);
      return tripData;

    } catch (error) {
      console.error(`Error fetching trip data for ${tripId}:`, error);
      return null;
    }
  };

  /**
   * Check if a vehicle's alwaysShow override is currently active based on its time window.
   * Uses the configured timezone (e.g. America/Los_Angeles) to evaluate.
   * 
   * For testing, add ?spoofTime=2026-06-28T12:00:00 to the URL to simulate a specific time
   * (interpreted in the vehicle's configured timezone).
   */
  const isAlwaysShowActive = (vehicle) => {
    if (!vehicle.alwaysShow || !vehicle.alwaysShow.enabled) return false;

    const { startTime, endTime, timezone } = vehicle.alwaysShow;
    if (!startTime || !endTime) return false;

    try {
      const params = new URLSearchParams(window.location.search);
      const spoofTime = params.get('spoofTime');

      let nowInTz;

      if (spoofTime) {
        // Use the spoofed time directly (treated as being in the target timezone)
        nowInTz = new Date(spoofTime);
        console.log(`🕐 Using spoofed time: ${spoofTime}`);
      } else {
        // Get the current time formatted in the target timezone
        const now = new Date();
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone || 'America/Los_Angeles',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });

        const parts = formatter.formatToParts(now);
        const get = (type) => parts.find(p => p.type === type)?.value;
        nowInTz = new Date(`${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`);
      }

      const start = new Date(startTime);
      const end = new Date(endTime);

      return nowInTz >= start && nowInTz <= end;
    } catch (e) {
      console.error(`Error evaluating alwaysShow for vehicle ${vehicle.id}:`, e);
      return false;
    }
  };

  /**
   * Find vehicles and load their trip data
   */
  const fetchLiveVehicleData = async () => {
    try {
      console.log('🚀 Fetching live vehicle data...');

      const foundVehicles = [];
      const loadedRoutes = [];
      const vehiclesNotFound = [];

      // Process each campaign vehicle
      for (const vehicle of campaignData.vehicles) {
        // Respect vehicle active flag: false means hide/skip
        if (vehicle.active === false) {
          continue;
        }
        console.log(`Looking for vehicle ${vehicle.id} on routes: ${vehicle.routes.join(', ')}`);

        let vehicleFound = false;
        const alwaysShowActive = isAlwaysShowActive(vehicle);

        if (alwaysShowActive) {
          console.log(`🏳️‍🌈 Vehicle ${vehicle.id} has alwaysShow active — will show regardless of trip ID`);
        }

        // Check each route the vehicle might be on
        for (const routeId of vehicle.routes) {
          if (vehicleFound) break;

          const routeVehicles = await fetchRouteVehicles(routeId);
          const liveVehicle = routeVehicles.find(v => v.id === vehicle.id);

          if (liveVehicle) {
            console.log(`✅ Found vehicle ${vehicle.id} on route ${routeId}`);

            // Extract trip ID from live vehicle data
            const tripId = liveVehicle.tripTag || liveVehicle.trip_id || liveVehicle.tripId;

            if (tripId) {
              // Fetch trip data (gracefully handle missing trip JSON)
              const tripData = await fetchTripData(tripId);

              if (tripData) {
                // Add vehicle with live data and full trip info
                foundVehicles.push({
                  ...vehicle,
                  currentPosition: [liveVehicle.lat, liveVehicle.lon],
                  heading: typeof liveVehicle.heading === 'number' ? liveVehicle.heading : (Number(liveVehicle.heading) || 0),
                  predictable: liveVehicle.predictable,
                  breakpointing: liveVehicle.breakpointing,
                  route: liveVehicle.route,
                  tripId: tripId,
                  tripData: tripData
                });

                // Add route to render (avoid duplicates)
                if (!loadedRoutes.find(r => r.trip_id === tripId)) {
                  loadedRoutes.push({
                    id: tripData.route_id,
                    name: `${tripData.route_id} ${tripData.trip_headsign}`,
                    trip_id: tripId,
                    coordinates: tripData.route_path ?
                      tripData.route_path.coordinates.map(coord => [coord[1], coord[0]]) : [],
                    stops: tripData.stops || [],
                    color: getRouteColor(tripData.route_id),
                    isFallback: false
                  });
                }
              } else if (alwaysShowActive) {
                // Trip ID exists but trip JSON file not available — still show the vehicle
                console.log(`🏳️‍🌈 Vehicle ${vehicle.id} trip data unavailable for ${tripId}, showing anyway (alwaysShow)`);

                // Allow spoofing the vehicle position via URL param for testing
                const params = new URLSearchParams(window.location.search);
                const spoofPos = params.get('spoofPosition');
                let vehicleLat = liveVehicle.lat;
                let vehicleLon = liveVehicle.lon;
                if (spoofPos) {
                  const [sLat, sLon] = spoofPos.split(',').map(Number);
                  if (Number.isFinite(sLat) && Number.isFinite(sLon)) {
                    vehicleLat = sLat;
                    vehicleLon = sLon;
                    console.log(`🏳️‍🌈 Using spoofed position: [${vehicleLat}, ${vehicleLon}]`);
                  }
                }

                foundVehicles.push({
                  ...vehicle,
                  currentPosition: [vehicleLat, vehicleLon],
                  heading: typeof liveVehicle.heading === 'number' ? liveVehicle.heading : (Number(liveVehicle.heading) || 0),
                  predictable: liveVehicle.predictable,
                  breakpointing: liveVehicle.breakpointing,
                  route: liveVehicle.route,
                  tripId: tripId,
                  tripData: null
                });
              }
            } else if (alwaysShowActive) {
              // No trip ID but alwaysShow is active — show the vehicle with its position
              console.log(`🏳️‍🌈 Vehicle ${vehicle.id} has no trip ID, showing anyway (alwaysShow)`);

              // Allow spoofing the vehicle position via URL param for testing
              const params = new URLSearchParams(window.location.search);
              const spoofPos = params.get('spoofPosition');
              let vehicleLat = liveVehicle.lat;
              let vehicleLon = liveVehicle.lon;
              if (spoofPos) {
                const [sLat, sLon] = spoofPos.split(',').map(Number);
                if (Number.isFinite(sLat) && Number.isFinite(sLon)) {
                  vehicleLat = sLat;
                  vehicleLon = sLon;
                  console.log(`🏳️‍🌈 Using spoofed position: [${vehicleLat}, ${vehicleLon}]`);
                }
              }

              foundVehicles.push({
                ...vehicle,
                currentPosition: [vehicleLat, vehicleLon],
                heading: typeof liveVehicle.heading === 'number' ? liveVehicle.heading : (Number(liveVehicle.heading) || 0),
                predictable: liveVehicle.predictable,
                breakpointing: liveVehicle.breakpointing,
                route: liveVehicle.route,
                tripId: null,
                tripData: null
              });
            } else {
              console.log(`Vehicle ${vehicle.id} found but no trip ID available`);
              // Treat vehicles without trip data the same as vehicles not found
              // They will get fallback route treatment
              vehiclesNotFound.push(vehicle);
            }

            vehicleFound = true;
          }
        }

        if (!vehicleFound) {
          if (alwaysShowActive) {
            // Vehicle not found on its listed routes — search all agency vehicles
            console.log(`🏳️‍🌈 Vehicle ${vehicle.id} not on listed routes, searching all agency vehicles...`);
            const allVehicles = await fetchAllAgencyVehicles();
            const liveVehicle = allVehicles.find(v => String(v.id) === String(vehicle.id));

            if (liveVehicle) {
              console.log(`🏳️‍🌈 Found vehicle ${vehicle.id} in all-agency feed at [${liveVehicle.lat}, ${liveVehicle.lon}]`);
              const tripId = liveVehicle.tripTag || liveVehicle.trip_id || liveVehicle.tripId;
              let tripData = null;

              if (tripId) {
                tripData = await fetchTripData(tripId);
                if (!tripData) {
                  console.log(`🏳️‍🌈 Trip data unavailable for ${tripId}, showing vehicle without route`);
                }
              }

              // Allow spoofing the vehicle position via URL param for testing
              const params = new URLSearchParams(window.location.search);
              const spoofPos = params.get('spoofPosition');
              let vehicleLat = liveVehicle.lat;
              let vehicleLon = liveVehicle.lon;
              if (spoofPos) {
                const [sLat, sLon] = spoofPos.split(',').map(Number);
                if (Number.isFinite(sLat) && Number.isFinite(sLon)) {
                  vehicleLat = sLat;
                  vehicleLon = sLon;
                  console.log(`🏳️‍🌈 Using spoofed position: [${vehicleLat}, ${vehicleLon}]`);
                }
              }

              foundVehicles.push({
                ...vehicle,
                currentPosition: [vehicleLat, vehicleLon],
                heading: typeof liveVehicle.heading === 'number' ? liveVehicle.heading : (Number(liveVehicle.heading) || 0),
                predictable: liveVehicle.predictable,
                breakpointing: liveVehicle.breakpointing,
                route: liveVehicle.route,
                tripId: tripId || null,
                tripData: tripData
              });

              if (tripData && tripId && !loadedRoutes.find(r => r.trip_id === tripId)) {
                loadedRoutes.push({
                  id: tripData.route_id,
                  name: `${tripData.route_id} ${tripData.trip_headsign}`,
                  trip_id: tripId,
                  coordinates: tripData.route_path ?
                    tripData.route_path.coordinates.map(coord => [coord[1], coord[0]]) : [],
                  stops: tripData.stops || [],
                  color: getRouteColor(tripData.route_id),
                  isFallback: false
                });
              }
            } else {
              console.log(`🏳️‍🌈 Vehicle ${vehicle.id} not found in all-agency feed either`);
              vehiclesNotFound.push(vehicle);
            }
          } else {
            console.log(`❌ Vehicle ${vehicle.id} not found on any route - likely out of service`);
            vehiclesNotFound.push(vehicle);
          }
        }
      }

      // Load fallback routes for vehicles not found
      if (vehiclesNotFound.length > 0) {
        console.log('🔄 Loading fallback routes for out-of-service vehicles...');
        
        const fallbackRoutes = await loadFallbackRoutes(vehiclesNotFound);
        loadedRoutes.push(...fallbackRoutes);
      }

      setActiveVehicles(foundVehicles);
      setTripRoutes(loadedRoutes);

      // Calculate map bounds from loaded routes
      calculateMapBounds(loadedRoutes, foundVehicles);

    } catch (error) {
      console.error('Error fetching live vehicle data:', error);
    }
  };

  /**
   * Fetch all vehicles currently operating on the configured routes.
   */
  const fetchLiveFleetVehicleData = async () => {
    try {
      const routesFleet = campaignData.routesFleet;
      if (!Array.isArray(routesFleet) || routesFleet.length === 0) {
        setFleetVehicles([]);
        return;
      }

      console.log('🚍 Fetching live route fleet vehicle data...');

      const all = [];
      const tripCache = new Map();
      for (const route of routesFleet) {
        const routeId = route.id;
        if (!routeId) continue;

        const vehicles = await fetchRouteVehicles(routeId);
        for (const v of vehicles) {
          const tripTagOk = typeof v.tripTag === 'string' && v.tripTag.length > 3;
          const predictableOk = v.predictable === true;
          const breakpointingOk = v.breakpointing === false;
          if (!tripTagOk || !predictableOk || !breakpointingOk) {
            continue;
          }

          const tripId = v.tripTag || v.trip_id || v.tripId;
          let tripData = null;
          if (tripId) {
            if (tripCache.has(tripId)) {
              tripData = tripCache.get(tripId);
            } else {
              tripData = await fetchTripData(tripId);
              tripCache.set(tripId, tripData);
            }
          }

          all.push({
            id: v.id,
            routeId,
            icon: route.icon,
            arrowColor: getRouteColor(routeId),
            currentPosition: [v.lat, v.lon],
            heading: typeof v.heading === 'number' ? v.heading : Number(v.heading),
            route: v.route,
            tripId,
            tripData
          });
        }
      }

      // De-dupe by vehicle id
      const byId = new Map();
      for (const v of all) {
        if (!byId.has(v.id)) byId.set(v.id, v);
      }

      const deduped = Array.from(byId.values());
      setFleetVehicles(deduped);

      // Use fleet vehicles to set map bounds for routes_only mode
      if (isRoutesOnlyMode) {
        calculateMapBounds(fleetTripRoutes, deduped);
      }
    } catch (error) {
      console.error('Error fetching live fleet vehicle data:', error);
    }
  };

  const loadFallbackRoutes = async (vehiclesNotFound) => {
    const fallbackRoutes = [];
    const processedRoutes = new Set();
    for (const vehicle of vehiclesNotFound) {
      for (const routeId of vehicle.routes) {
        // Avoid duplicate routes
        if (processedRoutes.has(routeId)) continue;
        processedRoutes.add(routeId);

        const routeInfo = routesData[routeId];

        if (routeInfo && routeInfo['default-trip']) {
          try {
            const tripData = await fetchTripData(routeInfo['default-trip']);

            if (tripData) {
              fallbackRoutes.push({
                id: routeId,
                name: `Route ${routeId} (Default)`,
                trip_id: routeInfo['default-trip'],
                coordinates: tripData.route_path ?
                  tripData.route_path.coordinates.map(coord => [coord[1], coord[0]]) : [],
                stops: tripData.stops || [],
                color: routeInfo['default-color'] || '#D3D3D3',
                isFallback: true
              });

              console.log(`✅ Loaded fallback route ${routeId}: Trip ${routeInfo['default-trip']} (${routeInfo['default-trip-stop-count']} stops)`);
            }
          } catch (error) {
            console.error(`❌ Failed to load fallback route ${routeId}:`, error);
          }
        }
      }
    }

    return fallbackRoutes;
  };

  /**
   * Get color for route based on route ID
   */
  const getRouteColor = (routeId) => {
    // Try to get color from campaign routes config
    if (campaignData.routesConfig) {
      const routeConfig = campaignData.routesConfig.find(r => r.id === routeId);
      if (routeConfig && routeConfig.color) {
        return routeConfig.color;
      }
    }
    
    // Fallback to default colors if routes config not available
    const fallbackColors = {
      '5': '#FF6B35',
      'N': '#0072CE',
      '7': '#8CC63F'
    };
    return fallbackColors[routeId] || '#666666';
  };

  // Compute fitBounds padding with extra space for the bottom drawer
  const getDrawerHeight = () => {
    const vehicleEl = document.getElementById('vehicle-status-drawer');
    const routeEl = document.getElementById('route-status-drawer');
    const vehicleHeight = vehicleEl ? vehicleEl.offsetHeight : 0;
    const routeHeight = routeEl ? routeEl.offsetHeight : 0;
    return vehicleHeight + routeHeight;
  };

  const getFitPadding = () => {
    const bottom = getDrawerHeight();
    return {
      paddingTopLeft: [20, 20],
      paddingBottomRight: [20, 20 + bottom]
    };
  };

  /**
   * Calculate map bounds from routes and vehicles
   */
  const calculateMapBounds = (routes, vehicles) => {
    // In TV mode, set the bounds once and do not auto-center thereafter
    if (isTvMode) {
      if (!hasAppliedTvBounds.current) {
        setDefaultBounds(TV_BOUNDS);
        if (mapRef.current) {
          mapRef.current.fitBounds(TV_BOUNDS, getFitPadding());
        }
        hasAppliedTvBounds.current = true;
      }
      return;
    }

    const allCoords = [];

    // Add route coordinates
    routes.forEach(route => {
      if (route.coordinates && route.coordinates.length > 0) {
        allCoords.push(...route.coordinates);
      }
      if (route.stops) {
        route.stops.forEach(stop => {
          allCoords.push([stop.stop_lat, stop.stop_lon]);
        });
      }
    });

    // Add vehicle positions
    vehicles.forEach(vehicle => {
      if (vehicle.currentPosition) {
        allCoords.push(vehicle.currentPosition);
      }
    });

    if (allCoords.length > 0) {
      const bounds = L.latLngBounds(allCoords);
      setDefaultBounds(bounds);

      // Also adjust current view with padding so content isn't hidden behind the drawer.
      if (mapRef.current) {
        if (isRoutesOnlyMode || isVehicleAndRoutesMode) {
          if (!hasAppliedRouteTrackingBounds.current && !hasUserInteractedWithMap.current) {
            suppressNextMoveEnd.current = true;
            mapRef.current.fitBounds(bounds, getFitPadding());
            hasAppliedRouteTrackingBounds.current = true;
          }
        } else {
          mapRef.current.fitBounds(bounds, getFitPadding());
        }
      }
    }
  };

  const fitInitialRouteTrackingBoundsIfNeeded = () => {
    if (!(isRoutesOnlyMode || isVehicleAndRoutesMode)) return;
    if (hasAppliedRouteTrackingBounds.current) return;
    if (hasUserInteractedWithMap.current) return;
    if (!mapRef.current || !defaultBounds) return;

    suppressNextMoveEnd.current = true;
    mapRef.current.fitBounds(defaultBounds, getFitPadding());
    hasAppliedRouteTrackingBounds.current = true;
  };

  const handleMapMoveEnd = () => {
    if (isTvMode) {
      // In TV mode, keep UI static; no reset button toggling
      return;
    }

    if ((isRoutesOnlyMode || isVehicleAndRoutesMode) && suppressNextMoveEnd.current) {
      suppressNextMoveEnd.current = false;
    } else if (isRoutesOnlyMode || isVehicleAndRoutesMode) {
      // Any move/zoom in these modes should lock the view (no future auto-fit on refresh)
      hasUserInteractedWithMap.current = true;
    }

    if (mapRef.current && defaultBounds) {
      const map = mapRef.current;
      const currentBounds = map.getBounds();
      const isAtDefaultView = defaultBounds.contains(currentBounds.getNorthEast()) &&
        defaultBounds.contains(currentBounds.getSouthWest());
      setShowResetButton(!isAtDefaultView);
    }
  };

  const resetMapView = () => {
    if (isTvMode) {
      // Do nothing in TV mode to keep the static view
      return;
    }
    if (mapRef.current && defaultBounds) {
      // Close any open popups
      mapRef.current.closePopup();
      
      // Reset map bounds
      mapRef.current.fitBounds(defaultBounds, getFitPadding());
      
      // Hide reset button after reset
      setShowResetButton(false);
    }
  };

  // Handle vehicle click from status drawer
  const handleVehicleClick = (vehicle, activeVehicle) => {
    if (!activeVehicle || !activeVehicle.currentPosition) {
      return;
    }

    // Close any open popups first
    if (mapRef.current) {
      mapRef.current.closePopup();
    }

    // Find the vehicle position
    const vehiclePosition = activeVehicle.currentPosition;
    
    // Use map to center on vehicle
    if (mapRef.current) {
      const map = mapRef.current;
      
      // Check if vehicle is within current view
      const currentBounds = map.getBounds();
      const vehicleLatLng = L.latLng(vehiclePosition[0], vehiclePosition[1]);
      
      if (!currentBounds.contains(vehicleLatLng)) {
        // In TV mode, do not re-center; keep static view
        if (!isTvMode && defaultBounds) {
          // Vehicle is not in view, zoom to show all routes and vehicles (default extent)
          map.fitBounds(defaultBounds, getFitPadding());
          // Find and open the popup for this vehicle after zooming to default extent
          setTimeout(() => {
            map.eachLayer((layer) => {
              if (layer instanceof L.Marker) {
                const markerPos = layer.getLatLng();
                if (Math.abs(markerPos.lat - vehiclePosition[0]) < 0.0001 && 
                    Math.abs(markerPos.lng - vehiclePosition[1]) < 0.0001) {
                  layer.openPopup();
                }
              }
            });
          }, 300);
        }
      } else {
        // Vehicle is already in view, just open its popup without zooming
        setTimeout(() => {
          map.eachLayer((layer) => {
            if (layer instanceof L.Marker) {
              const markerPos = layer.getLatLng();
              if (Math.abs(markerPos.lat - vehiclePosition[0]) < 0.0001 && 
                  Math.abs(markerPos.lng - vehiclePosition[1]) < 0.0001) {
                layer.openPopup();
              }
            }
          });
        }, 100);
      }
    }
  };

  const routesToRender = useMemo(() => {
    const base = !shouldPollRouteFleets ? tripRoutes : [...(fleetTripRoutes || []), ...(tripRoutes || [])];
    const merged = [...base, ...(externalRoutes || [])];
    const byKey = new Map();
    for (const r of merged) {
      const key = r.trip_id || r.id;
      if (!byKey.has(key)) byKey.set(key, r);
    }
    return Array.from(byKey.values());
  }, [shouldPollRouteFleets, fleetTripRoutes, tripRoutes, externalRoutes]);

  const routesFleetForTray = useMemo(() => {
    const internal = Array.isArray(campaignData.routesFleet)
      ? campaignData.routesFleet.filter(r => r && r.id).map(r => ({ ...r, color: getRouteColor(r.id) }))
      : [];
    
    // Add external routes to the tray
    const external = Array.isArray(externalRoutes)
      ? externalRoutes.map(r => ({ id: r.id, color: r.color, icon: 'bus.svg', isExternal: true }))
      : [];
    
    return [...internal, ...external];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignData.routesFleet, externalRoutes]);

  const filteredFleetVehicles = useMemo(() => {
    const internal = (!Array.isArray(fleetVehicles) || fleetVehicles.length === 0) ? [] :
      (!activeFleetRouteIds || activeFleetRouteIds.size === 0) ? [] :
      fleetVehicles.filter(v => v && v.routeId != null && activeFleetRouteIds.has(String(v.routeId)));
    
    // Include external vehicles (from 511 cacher etc.) filtered by active route IDs
    const external = (!Array.isArray(externalVehicles) || externalVehicles.length === 0) ? [] :
      (!activeFleetRouteIds || activeFleetRouteIds.size === 0) ? [] :
      externalVehicles.filter(v => v && v.routeId != null && activeFleetRouteIds.has(String(v.routeId)));
    
    return [...internal, ...external];
  }, [fleetVehicles, externalVehicles, activeFleetRouteIds]);

  const filteredStaticPoints = useMemo(() => {
    const pts = campaignData.staticContent && Array.isArray(campaignData.staticContent.point_data)
      ? campaignData.staticContent.point_data
      : [];

    if (!pts.length) return [];

    // In static_only mode, mark pins as greyed based on type toggles
    // All pins remain — greyed ones are still tappable but visually dimmed
    if (isStaticOnlyMode && districtLayersEnabled) {
      return pts.filter(pt => !!pt).map((pt) => {
        const isHQ = pt.type === 'Match HQ';
        const isGem = pt.type === 'Hidden Gem';
        const isWP = pt.type === 'Watch Party';
        const greyed = (isHQ && !showMatchHQ) || (isGem && !showHiddenGem) || (isWP && !showWatchParties);
        return { ...pt, greyed };
      });
    }

    // Cup campaign: hide watch parties or venues/countries based on toggles
    if (isStaticOnlyMode && campaignName === 'cup' && !districtLayersEnabled) {
      return pts.filter(pt => {
        if (!pt) return false;
        const isWP = pt.type === 'Viewing License';
        const isVenue = pt.type === 'Bar';

        if (isWP && !showWatchParties) return false;
        if (isVenue) {
          if (!showCupVenues) return false;
          if (cupFiltersActive && enabledCountries.size > 0) {
            const affinities = pt.national_affinities || [];
            const matchesFilter = affinities.some(c => enabledCountries.has(c));
            if (!matchesFilter) return false;
          }
        }
        return true;
      });
    }

    // In venue-toggle mode, filter by active venue ids directly
    if (venueToggleMode) {
      return pts.filter((pt) => {
        if (!pt) return false;
        if (!pt.id) return true;
        return activeVenueIds.has(String(pt.id));
      });
    }

    return pts.filter((pt) => {
      if (!pt) return false;
      const corr = pt.cooresponding_route;
      if (corr == null) return true;
      if (!activeFleetRouteIds) return true;
      const key = String(corr);
      return activeFleetRouteIds.has(key);
    });
  }, [campaignData.staticContent, activeFleetRouteIds, venueToggleMode, activeVenueIds, isStaticOnlyMode, districtLayersEnabled, showMatchHQ, showHiddenGem, showWatchParties, districtNameToId, campaignName, showCupVenues, enabledCountries, cupFiltersActive]);

  const userLocationColor = useMemo(() => {
    const cfg = campaignData.config;
    if (!cfg) return null;

    const direct = cfg.userLocationColor || cfg.user_location_color;
    if (typeof direct === 'string' && direct.trim().length > 0) return direct;

    const nested = cfg.dynamicOverlays
      && cfg.dynamicOverlays.ui
      && (cfg.dynamicOverlays.ui.user_pin_color || cfg.dynamicOverlays.ui.userPinColor);
    if (typeof nested === 'string' && nested.trim().length > 0) return nested;

    return null;
  }, [campaignData.config]);

  const userLocationTargetZoom = useMemo(() => {
    const cfg = campaignData.config;
    if (!cfg) return null;

    const nested = cfg.dynamicOverlays
      && cfg.dynamicOverlays.ui
      && (cfg.dynamicOverlays.ui.user_pin_zoom || cfg.dynamicOverlays.ui.userPinZoom);
    const z = Number(nested);
    return Number.isFinite(z) ? z : null;
  }, [campaignData.config]);

  const openStaticPointPopup = useCallback((pt) => {
    if (!pt) return;
    const lat = Number(pt.lat);
    const lng = Number(pt.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const key = (pt.id != null && String(pt.id).trim().length > 0) ? String(pt.id) : `${lat},${lng}`;
    const marker = staticMarkerRefMapRef.current ? staticMarkerRefMapRef.current.get(key) : null;
    if (marker && typeof marker.openPopup === 'function') {
      marker.openPopup();
    }
  }, []);

  // Deep-link: if URL has ?pin=42, zoom to that point and open its popup
  const lastDeepLinkPin = useRef('');
  const pinFromRouter = searchParams.get('pin') || '';
  // Fallback: read directly from window.location in case React Router didn't pick it up
  const pinFromWindow = new URLSearchParams(window.location.search).get('pin') || '';
  const pinParam = pinFromRouter || pinFromWindow;

  console.log('[deep-link] pinFromRouter:', pinFromRouter, '| pinFromWindow:', pinFromWindow, '| pinParam:', pinParam, '| window.location.search:', window.location.search, '| window.location.href:', window.location.href);

  useEffect(() => {
    console.log('[deep-link] Effect running | staticContent:', !!campaignData.staticContent, '| mapRef:', !!mapRef.current, '| pinParam:', pinParam, '| lastPin:', lastDeepLinkPin.current, '| loading:', loading);
    if (!campaignData.staticContent) return;
    if (!pinParam || pinParam === lastDeepLinkPin.current) return;    const pts = campaignData.staticContent.point_data || [];
    const pt = pts.find(p => String(p.id) === pinParam);
    if (!pt) return;

    const lat = Number(pt.lat);
    const lng = Number(pt.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    lastDeepLinkPin.current = pinParam;

    console.log('[deep-link] Target found:', pt.id, pt.title, 'at', lat, lng);

    // Two-phase zoom: start at 18, if still clustered zoom to max (20)
    const waitAndZoom = () => {
      if (!mapRef.current) {
        setTimeout(waitAndZoom, 100);
        return;
      }
      const map = mapRef.current;
      map.setView([lat, lng], 18, { animate: true });

      const checkAndOpen = (zoomedMax) => {
        const key = String(pt.id);
        const marker = staticMarkerRefMapRef.current ? staticMarkerRefMapRef.current.get(key) : null;
        if (!marker) return;

        const markerEl = marker.getElement ? marker.getElement() : null;
        if (markerEl && markerEl.offsetParent !== null) {
          // Marker is unclustered and visible — open its popup
          marker.openPopup();
          console.log('[deep-link] Popup opened directly');
          return;
        }

        if (!zoomedMax) {
          // Still clustered at zoom 18 — zoom to max and try again
          console.log('[deep-link] Still clustered at 18, zooming to 20');
          map.setView([lat, lng], 20, { animate: true });
          setTimeout(() => checkAndOpen(true), 800);
          return;
        }

        // At max zoom and still clustered (identical coordinates) — open standalone popup
        console.log('[deep-link] At max zoom, opening standalone popup');
        const popupContent = `<div style="min-width:180px;max-width:240px;">
          <div style="margin-bottom:4px;">
            <span style="font-size:0.85rem;font-weight:600;color:#41392f;">${pt.title || ''}</span>
          </div>
          ${pt.address ? `<div style="font-size:0.75rem;color:#90795b;margin-bottom:6px;">${pt.address.replace(/,?\s*(San Francisco|SF),?\s*(CA)?\s*\d*/i, '').trim()}</div>` : ''}
          <div><a href="https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent((pt.title || '') + ' ' + (pt.address || ''))}&travelmode=transit" target="_blank" rel="noreferrer" style="font-size:0.75rem;color:#2a6496;font-weight:500;text-decoration:none;">🚌 Directions</a></div>
        </div>`;
        L.popup()
          .setLatLng([lat, lng])
          .setContent(popupContent)
          .openOn(map);
      };

      // Wait for first zoom to settle, then check
      setTimeout(() => checkAndOpen(false), 800);
    };
    setTimeout(waitAndZoom, 300);
  }, [campaignData.staticContent, openStaticPointPopup, pinParam, loading]);

  const shouldShowRouteTray = (showRouteTray && (isRoutesOnlyMode || isVehicleAndRoutesMode));

  // Build a route color lookup map for the NextMatchDrawer
  const routeColorMap = useMemo(() => {
    const map = new Map();
    // From routes-config.json
    if (Array.isArray(campaignData.routesConfig)) {
      for (const r of campaignData.routesConfig) {
        if (r && r.id && r.color) map.set(String(r.id), r.color);
      }
    }
    // From external routes config
    const extCfg = campaignData.config && campaignData.config.externalRoutes;
    if (extCfg && Array.isArray(extCfg.routes)) {
      for (const r of extCfg.routes) {
        if (r && r.id && r.color) map.set(String(r.id), r.color);
      }
    }
    return map;
  }, [campaignData.routesConfig, campaignData.config]);
  const shouldShowVehicleTray = (showVehicleTray && (campaignMode === 'vehicles_only' || isVehicleAndRoutesMode));
  const trayHeight = 200;

  if (loading) {
    return <LoadingSpinner message={`Loading ${campaignName} campaign...`} />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  if (!isStaticOnlyMode && !defaultBounds && tripRoutes.length === 0 && fleetVehicles.length === 0) {
    return <LoadingSpinner message="Looking for active vehicles..." />;
  }

  // Use fallback bounds if no routes loaded yet. In TV mode, TV_BOUNDS are provided once and memoized.
  const mapBounds = isTvMode
    ? TV_BOUNDS
    : (defaultBounds || L.latLngBounds([
        [37.7049, -122.5280], // Southwest corner of SF
        [37.8199, -122.3588]  // Northeast corner of SF
      ]));

  // In routes_only / vehicle_and_routes we do NOT want React-Leaflet to keep re-applying bounds
  // on refresh. We instead rely on our one-time calculateMapBounds() fit.
  // In static_only mode, we just center on SF with no bounds tracking.
  const useControlledBounds = !isStaticOnlyMode && (isTvMode || campaignMode === 'vehicles_only');

  return (
    <div style={{ height: '100vh', width: '100vw', position: 'relative' }}>
      <MapContainer
        ref={mapRef}
        {...(useControlledBounds
          ? { bounds: mapBounds }
          : isStaticOnlyMode
            ? { center: [37.761, -122.4194], zoom: 13 }
            : { center: [37.7749, -122.4194], zoom: 13 })}
        maxZoom={isStaticOnlyMode ? 20 : 18}
        style={{ height: '100%', width: '100%' }}
        zoomControl={true}
        scrollWheelZoom={true}
        doubleClickZoom={true}
        touchZoom={true}
        dragging={true}
        whenReady={() => {
          fitInitialRouteTrackingBoundsIfNeeded();
        }}
      >
        <CenterOnUserLocationControl
          userLocation={userLocation}
          setUserLocation={setUserLocation}
          targetZoom={userLocationTargetZoom != null ? userLocationTargetZoom : 16}
        />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          maxZoom={isStaticOnlyMode ? 20 : 18}
        />

        {/* Handle map events for reset button */}
        <MapEventHandler onMapMove={handleMapMoveEnd} defaultBounds={defaultBounds} />

        {/* Render routes from trip data */}
        {!isStaticOnlyMode && (
          <RouteLayer routes={routesToRender} activeRouteIds={activeFleetRouteIds} />
        )}

        {/* Render active vehicles */}
        {shouldPollVehiclesOnly && (
          <VehicleLayer
            vehicles={activeVehicles}
            campaignName={campaignName}
            vehicleArrowColor={vehicleArrowColor}
          />
        )}

        {/* Render route fleet vehicles (routes_only / vehicle_and_routes modes) */}
        {!isStaticOnlyMode && (showRouteTray || isVehicleAndRoutesMode || isRoutesOnlyMode) && (
          <FleetVehicleLayer
            vehicles={filteredFleetVehicles}
            campaignName={campaignName}
            arrowColor={vehicleArrowColor}
            isRoutesOnlyMode={isRoutesOnlyMode}
            staticPoints={filteredStaticPoints}
            onOpenStaticPointPopup={openStaticPointPopup}
            hideNearestPoint={isEventFocusMode}
          />
        )}

        <Pane name="staticContentPane" style={{ zIndex: 650 }}>
          {/* Render static overlay points (when enabled) */}
          {staticOverlaysEnabled && campaignData.staticContent && (
            <StaticContentLayer
              points={filteredStaticPoints}
              campaignName={campaignName}
              pane="staticContentPane"
              markerRefMapRef={staticMarkerRefMapRef}
              isRoutesOnlyMode={isRoutesOnlyMode}
              campaignMode={campaignMode}
              routeColorsData={campaignData.routeColors}
              clusterGroupRef={clusterGroupRef}
              teams={campaignData.staticContent._teams || null}
            />
          )}

          {/* User location marker */}
          {userLocation && (
            <VehicleLayer
              vehicles={[{
                id: 'user-location',
                name: 'Your Location',
                currentPosition: userLocation,
                icon: 'user-location.png'
              }]}
              campaignName={campaignName}
              isUserLocation={true}
              userLocationColor={userLocationColor}
              pane="staticContentPane"
            />
          )}
        </Pane>
      </MapContainer>

      {/* UI Overlays */}
      {showResetButton && !isTvMode && (
        <ResetZoomButton onClick={resetMapView} />
      )}

      {/* Persistent Vehicle Status Drawer */}
      {shouldShowVehicleTray && (
        <VehicleStatusDrawer
          vehicles={campaignData.vehicles}
          activeVehicles={activeVehicles}
          loading={loading}
          mapRef={mapRef}
          onVehicleClick={handleVehicleClick}
          bottomOffset={(isVehicleAndRoutesMode && shouldShowRouteTray) ? trayHeight : 0}
          maxHeight={trayHeight}
        />
      )}

      {/* Persistent Route Status Drawer */}
      {shouldShowRouteTray && !venueToggleMode && (
        <RouteStatusDrawer
          routes={routesFleetForTray}
          activeRouteIds={activeFleetRouteIds}
          onToggleRoute={toggleFleetRoute}
          maxHeight={trayHeight}
          points={campaignData.staticContent ? campaignData.staticContent.point_data : null}
          trayConfig={staticOverlayTrayConfig}
          mapRef={mapRef}
          onOpenPointPopup={openStaticPointPopup}
        />
      )}

      {/* Venue Toggle Drawer (venue-toggle mode) */}
      {shouldShowRouteTray && venueToggleMode && !isEventFocusMode && (
        <VenueToggleDrawer
          venues={campaignData.staticContent ? campaignData.staticContent.point_data : []}
          activeVenueIds={activeVenueIds}
          onToggleVenue={toggleVenue}
          maxHeight={trayHeight}
          mapRef={mapRef}
          onOpenPointPopup={openStaticPointPopup}
          trayConfig={staticOverlayTrayConfig}
        />
      )}

      {/* Event Focus Drawer (event_focus mode) */}
      {shouldShowRouteTray && isEventFocusMode && (
        <EventFocusDrawer
          venues={campaignData.staticContent ? campaignData.staticContent.point_data : []}
          activeRouteIds={activeFleetRouteIds}
          onToggleRoute={toggleFleetRoute}
          routeColors={routeColorMap}
          maxHeight={trayHeight}
          mapRef={mapRef}
          onOpenPointPopup={openStaticPointPopup}
          trayConfig={staticOverlayTrayConfig}
        />
      )}

      {/* District Layer Drawer (static_only mode) */}
      {isStaticOnlyMode && districtLayersEnabled && (
        <DistrictLayerDrawer
          districts={districtList}
          onZoomToDistrict={zoomToDistrict}
          showMatchHQ={showMatchHQ}
          showHiddenGem={showHiddenGem}
          onToggleMatchHQ={() => setShowMatchHQ(prev => !prev)}
          onToggleHiddenGem={() => setShowHiddenGem(prev => !prev)}
          showWatchParties={showWatchParties}
          onToggleWatchParties={() => setShowWatchParties(prev => !prev)}
          layerDescriptions={campaignData.layerDescriptions}
        />
      )}

      {/* Cup campaign drawer */}
      {isStaticOnlyMode && campaignName === 'cup' && !districtLayersEnabled && (
        <CupDrawer
          points={campaignData.staticContent ? campaignData.staticContent.point_data : []}
          teams={campaignData.staticContent ? campaignData.staticContent._teams : []}
          showWatchParties={showWatchParties}
          onToggleWatchParties={() => setShowWatchParties(prev => !prev)}
          showVenues={showCupVenues}
          onToggleVenues={() => setShowCupVenues(prev => !prev)}
          enabledCountries={enabledCountries}
          onToggleCountry={(countryId) => {
            setEnabledCountries(prev => {
              const next = new Set(prev);
              if (next.has(countryId)) next.delete(countryId);
              else next.add(countryId);
              return next;
            });
            // Auto-activate filters on first country toggle
            if (!cupFiltersActive) setCupFiltersActive(true);
            if (!cupFiltersEverActivated) setCupFiltersEverActivated(true);
          }}
          filtersActive={cupFiltersActive}
          onToggleFiltersActive={() => setCupFiltersActive(prev => !prev)}
          filtersEverActivated={cupFiltersEverActivated}
          userLocation={userLocation}
          filteredPoints={filteredStaticPoints}
          onSelectPoint={(pt) => {
            if (!pt || !mapRef.current) return;
            const lat = Number(pt.lat);
            const lng = Number(pt.lng);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

            const map = mapRef.current;

            // Check if user is within SF proximity bounds
            const inSF = userLocation &&
              userLocation[0] >= 37.698051 && userLocation[0] <= 37.813568 &&
              userLocation[1] >= -122.528088 && userLocation[1] <= -122.343615;

            // Look up the marker instance
            const key = (pt.id != null && String(pt.id).trim().length > 0) ? String(pt.id) : `${lat},${lng}`;
            const marker = staticMarkerRefMapRef.current ? staticMarkerRefMapRef.current.get(key) : null;

            if (inSF) {
              // User in SF: fit bounds to include user pin + venue
              const bounds = L.latLngBounds([
                [userLocation[0], userLocation[1]],
                [lat, lng]
              ]);
              map.flyToBounds(bounds, { padding: [60, 60], maxZoom: 16, duration: 0.8 });
              // After fly, ensure cluster is open and popup shows
              setTimeout(() => {
                if (marker && clusterGroupRef.current) {
                  clusterGroupRef.current.zoomToShowLayer(marker, () => {
                    marker.openPopup();
                  });
                } else {
                  openStaticPointPopup(pt);
                }
              }, 900);
            } else {
              // Outside SF: use cluster's zoomToShowLayer for smart unclustering
              if (marker && clusterGroupRef.current) {
                // Smooth fly to the area first, then let cluster handle the uncluster zoom
                map.flyTo([lat, lng], Math.max(map.getZoom(), 14), { duration: 0.6 });
                setTimeout(() => {
                  clusterGroupRef.current.zoomToShowLayer(marker, () => {
                    marker.openPopup();
                  });
                }, 700);
              } else {
                // Fallback: greyed markers aren't in cluster group
                const currentZoom = map.getZoom();
                const targetZoom = Math.max(currentZoom, Math.min(15, currentZoom + 2));
                map.flyTo([lat, lng], targetZoom, { duration: 0.8 });
                setTimeout(() => openStaticPointPopup(pt), 900);
              }
            }
          }}
          onFilterZoom={(activePoints) => {
            if (!mapRef.current || !activePoints.length) return;
            const bounds = L.latLngBounds(
              activePoints.map(pt => [Number(pt.lat), Number(pt.lng)])
            );
            mapRef.current.flyToBounds(bounds, { padding: [40, 40], duration: 0.8 });
          }}
        />
      )}

      {/* Status indicator */}
      <div style={{
        position: 'absolute',
        top: '10px',
        left: '10px',
        background: 'rgba(0,0,0,0.7)',
        color: 'white',
        padding: '8px 12px',
        borderRadius: '4px',
        fontSize: '12px',
        zIndex: 1000,
        display: 'none',
      }}>
        Active Vehicles: {activeVehicles.length} | Routes: {tripRoutes.length}
      </div>
    </div>
  );
};

export default CampaignMap;
